import type {
  AddAllTurnaroundsToDeliveryRequest,
  AddTurnaroundSetToDeliveryRequest,
  DeliveriesRequestFilters,
  DeliveriesResponse,
  Delivery,
  DeliveryBody,
  DeliveryQuickCheckOutRequest,
  DeliveryQuickCheckOutResponse,
  DeliveryReceiveDispatchRequest,
  DeliveryReceiveDispatchResponse,
  DeliveryResponse,
  DeliveryReturnDetailsParams,
  NotifySetCheckedOutParams,
  PrintLabelRequestBody,
  SubmitOrderInspectionRequest,
  SubmitOrderInspectionResponse,
  UpdatedDelivery,
  ValidateSurgeryReferenceRequest,
  ValidateSurgeryReferenceResponse,
} from 'api/models/Deliveries';

import {
  AddImplantImagePathRequest,
  AddImplantImageToHospitalDeliveryResponse,
  AddImplantToDeliveryRequest,
  AddImplantToDeliveryResponse,
  SkipImplantCheckInReason,
  SkipImplantCheckOutReason,
} from 'api/models/Implants';
import {
  AddSetToOrderRequest,
  AddSetToOrderResponse,
  ConsolidateSetsRequest,
  ConsolidateSetsResponse,
  GetLastDmInspectionParams,
  GetLastDmInspectionResponse,
  GetSetManualsResponse,
  KitInstanceId,
  SaveInspectionResponse,
  SaveSetInspectionRequest,
  UpdateSetNameRequestBody,
} from 'api/models/Sets';
import { TimeStampResponse } from 'api/models/TimeStamps';
import {
  AddSadlTrayToOrderRequest,
  AddTrayToOrderRequest,
  DeleteTrayFromSetResponse,
  DeliverySetTrayStatus,
  InspectedTray,
  SaveTrayInspectionRequest,
  UpdateTrayNameRequestBody,
} from 'api/models/Trays';
import { TurnaroundListFilters, TurnaroundListResponse } from 'api/models/TurnaroundList';
import ApiClientBase from './ApiClientBase';
import { ORDERS_UPLOAD_AXIOS_OPTIONS } from './OrdersEndpoint';
import { getAxiosOptions } from 'services/APIService';

type DeliveryId = number | string;

class DeliveriesEndpoint extends ApiClientBase {
  apiUri = '/api/v2/hospital/deliveries';

  getDeliveries(params: DeliveriesRequestFilters) {
    const { status, supplier, ...rest } = params;
    const qs = new URLSearchParams(rest);

    // supplier is an object
    qs.set('supplier', supplier?.value || '');

    // this may be removed from the BE in future
    qs.set('selected_rate', 'true');

    // status may be an array
    const statuses = Array.isArray(status) ? status : status ? [status] : [];
    statuses.forEach(s => qs.append('status', s));

    return this.apiCall<DeliveriesResponse>({ uri: '/', data: qs });
  }

  getDelivery(deliveryId: DeliveryId) {
    return this.apiCall<Delivery>({
      uri: `/${deliveryId}/`,
      cfg: getAxiosOptions({ timeout: 60 * 1000 }), // 1 minute timeout for delivery details
    });
  }

  updateImplantStatus({ deliveryId, status }: { deliveryId: string; status: DeliverySetTrayStatus }) {
    return this.apiCall(`/${deliveryId}/implant/update_status/`, 'put', { status });
  }

  getTrayCapturePrintOut({
    deliveryId,
    tray,
  }: {
    deliveryId: string;
    tray?: { kitInstanceId?: string; trayInstanceId?: string };
  }) {
    // Ensure the request method is 'POST' since you need to send a body
    const url = `/${deliveryId}/trays/print_tray_image/`;

    // Prepare the body data to send the kitInstanceId if tray is provided
    const data = tray ? { kit_instance_id: tray.kitInstanceId, tray_instance_id: tray.trayInstanceId } : {};

    // Make the API call using 'POST' method and pass the data in the body
    return this.apiCall(url, 'post', data, { responseType: 'blob' });
  }

  // deliveryIds may be a - delimited string of delivery ids
  //  which may change in the future
  getPrintImplantsManifest({ deliveryIds }: { deliveryIds: string }) {
    const uri = `/${deliveryIds}/print_implants_manifest/`;
    return this.apiCall({
      uri,
      cfg: { responseType: 'blob', timeout: 2 * 60 * 1000 },
    });
  }

  getPrintDeliveryNoteReceived({ deliveryId }: { deliveryId: string | number }) {
    const uri = `/${deliveryId}/print_delivery_note_received/`;
    return this.apiCall({
      uri,
      cfg: { responseType: 'blob', timeout: 2 * 60 * 1000 },
    });
  }

  getPrintDeliveryNoteCheckOut({ deliveryId, returnDetails, returnedByRepId }: DeliveryReturnDetailsParams) {
    const uri = `/${deliveryId}/print_delivery_note_checkout/`;
    return this.apiCall({
      uri,
      method: 'post',
      cfg: { responseType: 'blob', timeout: 2 * 60 * 1000 },
      data: { checkoutDetails: returnDetails, returnedByRepId },
    });
  }

  printLabel({ deliveryId, data }: { deliveryId: string; data?: PrintLabelRequestBody }) {
    // this acts more like a get but it is a post since we send a request body
    const url = `/${deliveryId}/print_set_label/`;
    return this.apiCall(
      url,
      'post',
      data && {
        sets: data.trayLabelInfos, // poorly named in the api
        patientInitials: data.patientInitials,
      },
      { responseType: 'blob' },
    );
  }

  getPrintDeliveryNote({ deliveryId }: { deliveryId: string }) {
    const url = `/${deliveryId}/print_delivery_note/`;
    return this.apiCall(url, 'get', undefined, { responseType: 'blob' });
  }

  updateDelivery({ deliveryId, updatedDelivery }: { deliveryId: DeliveryId; updatedDelivery: UpdatedDelivery }) {
    return this.apiCall(`/${deliveryId}/`, 'put', updatedDelivery);
  }

  createDelivery(delivery: DeliveryBody) {
    return this.apiCall<DeliveryResponse>(`/`, 'post', delivery);
  }

  getTimeStamps(deliveryId: DeliveryId) {
    return this.apiCall<TimeStampResponse>(`/${deliveryId}/time_stamps`);
  }

  updateDeliveryInspection({
    deliveryId,
    inspection,
  }: {
    deliveryId: DeliveryId;
    inspection: SubmitOrderInspectionRequest;
  }) {
    return this.apiCall<SubmitOrderInspectionResponse>(`/${deliveryId}/inspection/`, 'put', inspection);
  }

  notifySetCheckedOut({ deliveryId, kitInstanceId, returnedByRepId, returnDetails }: NotifySetCheckedOutParams) {
    return this.apiCall<SubmitOrderInspectionResponse>(
      `/${deliveryId}/inspection/supplier_email_notification/`,
      'post',
      { kitInstanceId, returnedByRepId, returnDetails },
    );
  }

  notifyImplantCheckedOut({ deliveryId, returnedByRepId, returnDetails }: DeliveryReturnDetailsParams) {
    return this.apiCall<SubmitOrderInspectionResponse>(`/${deliveryId}/implant/supplier_email_notification/`, 'post', {
      returnedByRepId,
      returnDetails,
    });
  }

  // as above just without the kitInstanceId
  // purposely added to avoid
  notifySupplierCancelledDelivery({ deliveryId }: { deliveryId: DeliveryId }) {
    return this.apiCall<{
      status: string;
      message: string;
    }>(`/${deliveryId}/inspection/supplier_email_notification/`, 'post', {});
  }

  notifyDeliveryCheckedOut({ deliveryId, returnedByRepId, returnDetails }: DeliveryReturnDetailsParams) {
    return this.apiCall<SubmitOrderInspectionResponse>(
      `/${deliveryId}/inspection/supplier_email_notification/`,
      'post',
      { returnedByRepId, returnDetails },
    );
  }

  returnDelivery({ deliveryId, returnedByRepId, returnDetails }: DeliveryReturnDetailsParams) {
    return this.apiCall(`/${deliveryId}/return_delivery/`, 'put', { returnedByRepId, returnDetails });
  }

  addSetToDelivery({ deliveryId, set }: { deliveryId: DeliveryId; set: AddSetToOrderRequest }) {
    return this.apiCall<AddSetToOrderResponse>(`/${deliveryId}/set/`, 'post', set);
  }

  upsertImplantToDelivery({ deliveryId, implant }: { deliveryId: DeliveryId; implant: AddImplantToDeliveryRequest }) {
    return this.apiCall<AddImplantToDeliveryResponse>(`/${deliveryId}/implant/`, 'post', implant);
  }

  deleteImplantFromDelivery({ deliveryId, implantInstanceId }: { deliveryId: DeliveryId; implantInstanceId: string }) {
    return this.apiCall(`/${deliveryId}/implant/${implantInstanceId}/`, 'delete');
  }

  deleteImplantKitFromDelivery({
    deliveryId,
    implantInstanceIds,
  }: {
    deliveryId: DeliveryId;
    implantInstanceIds: string[];
  }) {
    return this.apiCall(`/${deliveryId}/implants/delete/`, 'post', implantInstanceIds);
  }

  addImplantImageToDelivery({ deliveryId, request }: { deliveryId: string; request: AddImplantImagePathRequest }) {
    return this.apiCall<AddImplantImageToHospitalDeliveryResponse>(
      `/${deliveryId}/implant/implant_images/`,
      'post',
      request,
    );
  }

  createDeliverySets({ deliveryId, numberOfSets }: { deliveryId: DeliveryId; numberOfSets: number }) {
    return this.apiCall<{
      status: string;
      message: string;
    }>(`/${deliveryId}/sets/`, 'post', {
      numberOfSets,
    });
  }

  deleteSetFromDelivery({ deliveryId, setId }: { deliveryId: string; setId: string }) {
    return this.apiCall(`/${deliveryId}/set/${setId}/`, 'delete');
  }

  updateSetInspection({
    setId,
    inspection,
  }: {
    deliveryId: DeliveryId;
    setId: string;
    inspection: SaveSetInspectionRequest;
  }) {
    return this.apiCall<SaveInspectionResponse>(`/sets/${setId}/inspection/`, 'put', inspection);
  }

  addTrayToSet({ setId, tray }: { setId: string; tray: AddTrayToOrderRequest | AddSadlTrayToOrderRequest }) {
    return this.apiCall<{
      kitInstanceId: string;
      providedTrayName: string;
      trayInstanceId: string;
    }>(`/set/${setId}/tray/`, 'post', tray);
  }

  updateTray({ trayId, tray }: { deliveryId: DeliveryId; trayId: string; tray: UpdateTrayNameRequestBody }) {
    return this.apiCall(`/trays/${trayId}/`, 'put', tray);
  }

  updateSet({
    deliveryId,
    kitInstanceId,
    set,
  }: {
    deliveryId: DeliveryId;
    kitInstanceId: KitInstanceId;
    set: UpdateSetNameRequestBody;
  }) {
    return this.apiCall(`${deliveryId}/set/${kitInstanceId}/`, 'put', set);
  }

  deleteTrayFromSet({ deliveryId, trayId }: { deliveryId: DeliveryId; trayId: string }) {
    return this.apiCall<DeleteTrayFromSetResponse>(`/${deliveryId}/tray/${trayId}/`, 'delete');
  }

  updateTrayInspection({ trayId, inspection }: { trayId: string; inspection: SaveTrayInspectionRequest }) {
    return this.apiCall<SaveInspectionResponse>(`/trays/${trayId}/inspection/`, 'put', inspection);
  }

  addTurnaroundSetToDelivery({
    deliveryId,
    request,
  }: {
    deliveryId: DeliveryId;
    request: AddTurnaroundSetToDeliveryRequest;
  }) {
    return this.apiCall(`/${deliveryId}/set/turnaround/`, 'post', request);
  }

  consolidateSets({ deliveryId, request }: { deliveryId: DeliveryId; request: ConsolidateSetsRequest }) {
    return this.apiCall<ConsolidateSetsResponse>(`/${deliveryId}/set/consolidate/`, 'post', request);
  }

  ungroupConsolidatedSet({ deliveryId, kitInstanceId }: { deliveryId: DeliveryId; kitInstanceId: string }) {
    return this.apiCall(`/${deliveryId}/set/consolidate/${kitInstanceId}/`, 'delete');
  }

  addTurnaroundImplantsToDelivery({ deliveryId, originDeliveryId }: { deliveryId: string; originDeliveryId: string }) {
    return this.apiCall(`/${deliveryId}/implant/turnaround/`, 'post', { originDeliveryId });
  }

  addAllTurnaroundsToDelivery({
    deliveryId,
    request,
  }: {
    deliveryId: DeliveryId;
    request: AddAllTurnaroundsToDeliveryRequest;
  }) {
    return this.apiCall(`/${deliveryId}/turnarounds/`, 'post', request);
  }

  quickCheckOut({ deliveryId, request }: { deliveryId: DeliveryId; request: DeliveryQuickCheckOutRequest }) {
    return this.apiCall<DeliveryQuickCheckOutResponse>(`/${deliveryId}/quick_checkout/`, 'put', request);
  }

  setQuickCheckOut({
    deliveryId,
    setKitInstanceId,
    request,
  }: {
    deliveryId: string;
    setKitInstanceId: string;
    request: DeliveryQuickCheckOutRequest;
  }) {
    return this.apiCall<DeliveryQuickCheckOutResponse>(
      `/${deliveryId}/sets/${setKitInstanceId}/quick_checkout/`,
      'put',
      request,
    );
  }

  receiveDispatch({ deliveryId, request }: { deliveryId: DeliveryId; request: DeliveryReceiveDispatchRequest }) {
    return this.apiCall<DeliveryReceiveDispatchResponse>(`/${deliveryId}/receive_dispatch_delivery/`, 'put', request);
  }

  deleteDelivery({ deliveryId }: { deliveryId: string }) {
    return this.apiCall(`/${deliveryId}/`, 'delete');
  }

  deliveryCancelSurgery({ deliveryId }: { deliveryId: string }) {
    return this.apiCall<{
      deliveryId: number;
      status: string;
    }>(`/${deliveryId}/cancel_surgery/`, 'put');
  }

  validateSurgeryReference(payload: ValidateSurgeryReferenceRequest) {
    return this.apiCall<ValidateSurgeryReferenceResponse>('/validate_surgery_id/', 'post', payload);
  }

  validateTrayReference(deliveryId: string, trayReference: number) {
    return this.apiCall<Response>(
      `${deliveryId}/trays/tray_reference_validation/?tray_reference=${trayReference}`,
      'get',
    );
  }

  uploadGcpFile(gs: string, dataUri: string, metadata?: Record<string, string>) {
    const [scheme, data] = dataUri.split(',');
    const isEncoded = scheme.includes('base64');
    const mimeType = scheme.split(':')[1].split(';')[0];
    const payload = { gs, isEncoded, mimeType, data, metadata };
    return this.apiCall('/storage/upload-file/', 'put', payload, ORDERS_UPLOAD_AXIOS_OPTIONS);
  }

  saveAllTrayInspections({
    deliveryId,
    setKitInstanceId,
    payload,
  }: {
    deliveryId: string;
    setKitInstanceId: string;
    payload: InspectedTray;
  }) {
    return this.apiCall<{
      status: string;
      message: string;
    }>(`/${deliveryId}/sets/${setKitInstanceId}/trays/inspections/`, 'post', payload);
  }

  saveAllSetsInspections({ deliveryId, payload }: { deliveryId: string; payload: Record<string, InspectedTray> }) {
    return this.apiCall<{
      status: string;
      message: string;
    }>(`/${deliveryId}/sets/inspections/`, 'post', payload);
  }

  getLastDmInspection({ setId, trayInstanceId }: GetLastDmInspectionParams) {
    return this.apiCall<GetLastDmInspectionResponse>(`set/${setId}/tray/${trayInstanceId}/last_dm_inspection/`);
  }

  async getSetManuals({ setId }: { setId: string }) {
    const res = await this.apiCall<{ setManuals?: GetSetManualsResponse }>(`/sets/${setId}/set_manuals/`);
    return res.setManuals;
  }

  getTurnaroundList(params: TurnaroundListFilters) {
    const { supplier, contents, ...restParams } = params;
    const searchParams = new URLSearchParams({
      ...restParams,
    });

    // The supplier search param is apparently an object
    if (supplier) {
      searchParams.set('supplier', supplier.value);
    }

    // DeliveryContent is an enum, so we need to convert it to a string
    if (contents) {
      searchParams.set('contents', contents.toString());
    }

    return this.apiCall<TurnaroundListResponse>({ uri: '/turnaround_items/', data: searchParams });
  }

  cancelTurnaround({
    deliveryId,
    kitInstanceIds,
    cancelImplants,
  }: {
    deliveryId: string;
    kitInstanceIds: string[];
    cancelImplants: boolean;
  }) {
    return this.apiCall(`/${deliveryId}/cancel_turnaround/`, 'post', { kitInstanceIds, cancelImplants });
  }

  saveImplantInspection({
    deliveryId,
    status,
    stage,
    gsImagePath,
  }: {
    deliveryId: string;
    status: DeliverySetTrayStatus;
    stage: 'CHECK_IN' | 'CHECK_OUT';
    gsImagePath: string;
  }) {
    return this.apiCall(`/${deliveryId}/implant/save_implant_inspection/`, 'put', { status, stage, gsImagePath });
  }

  skipImplantInspection({
    deliveryId,
    reason,
    count,
    date,
  }: {
    deliveryId: string;
    reason: SkipImplantCheckInReason | SkipImplantCheckOutReason;
    count: number | null;
    date: string;
  }) {
    return this.apiCall(`/${deliveryId}/implant/skip_implant_inspection/`, 'post', { reason, count, date });
  }
}

const deliveries = new DeliveriesEndpoint();
export default deliveries;
